import React, { useState } from "react";
import { motion } from "framer-motion";
import { Modal } from '../../components/modal/modal';
import { Card } from '../../components/card/card';
import Icon from '../../components/icon/icon';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function Information() {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState('');

  const openImageModal = (imageSrc) => {
    setSrc(imageSrc);
    setOpen(true);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div
        variants={itemVariants}
        className="border-b border-slate-200/60 pb-4"
      >
        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
          Información del Proyecto & Arquitectura
        </h1>
        <p className="text-xs text-slate-500 font-medium mt-0.5">
          Detalles sobre la metodología, equipo de desarrollo y prototipo de medición IoT
        </p>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card className="p-8 space-y-8">
          {/* Intro */}
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary-light text-primary mb-3">
              <Icon className="text-xs">bolt</Icon> Monitoreo Ambiental en Tiempo Real
            </span>
            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-3">
              Sistema de Medición de Emisiones Industriales (NOx y CO)
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Este proyecto desarrolla un sistema de monitoreo en tiempo real para la medición continua de
              óxidos de nitrógeno (NOx) y monóxido de carbono (CO) en entornos industriales, enfocado en las
              instalaciones de la <strong className="text-slate-800 font-semibold">Empresa Grupo Técnico Basse Ltda</strong>.
              Utilizando tecnología IoT de bajo costo, el prototipo permite la captura, transmisión y visualización
              de las concentraciones obtenidas, facilitando la detección temprana de niveles críticos y aportando a la salud ocupacional y gestión ambiental.
            </p>
          </div>

          {/* Section 1: About us (Image left, text right) */}
          <div className="flex flex-col md:flex-row gap-6 pt-6 border-t border-slate-100 items-center">
            <motion.div
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className="group relative shrink-0 overflow-hidden rounded-2xl border border-slate-200 shadow-sm cursor-pointer bg-slate-100"
              onClick={() => openImageModal('images/information/Info-3.jpg')}
            >
              <img
                src="images/information/Info-3.jpg"
                alt="Equipo de trabajo y pruebas de campo"
                className="h-72 w-56 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur-sm text-[11px] font-bold text-slate-900 shadow-md flex items-center gap-1.5">
                  <Icon>magnifying-glass-plus</Icon> Ampliar
                </span>
              </div>
            </motion.div>

            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-primary"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Equipo Multidisciplinar</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Acerca de Nosotros
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                Somos un equipo integrado por estudiantes de <strong className="text-slate-800">Ingeniería Ambiental</strong> e <strong className="text-slate-800">Ingeniería de Sistemas</strong>,
                cuya labor combina sinérgicamente conocimientos en electrónica, programación, análisis de datos y normatividad ambiental vigente.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Nuestro compromiso es implementar soluciones tecnológicas innovadoras, sostenibles y de bajo costo que fortalezcan el control preventivo y la gestión de la calidad del aire industrial.
              </p>
            </div>
          </div>

          {/* Section 2: Prototype (Text left, image right) */}
          <div className="flex flex-col md:flex-row-reverse gap-6 pt-6 border-t border-slate-100 items-center">
            <motion.div
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className="group relative shrink-0 overflow-hidden rounded-2xl border border-slate-200 shadow-sm cursor-pointer bg-slate-100"
              onClick={() => openImageModal('images/information/Info-4.jpg')}
            >
              <img
                src="images/information/Info-4.jpg"
                alt="Prototipo de hardware y circuito"
                className="h-64 w-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur-sm text-[11px] font-bold text-slate-900 shadow-md flex items-center gap-1.5">
                  <Icon>magnifying-glass-plus</Icon> Ampliar
                </span>
              </div>
            </motion.div>

            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Hardware & Telemetría</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Diseño y Arquitectura del Prototipo
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                El prototipo está basado en el sensor multigás <strong className="text-slate-800">MICS-6814</strong> para la detección de gases y el módulo Wi-Fi <strong className="text-slate-800">ESP8266</strong> para la conectividad inalámbrica,
                integrados en una estructura compacta que facilita su instalación en planta.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  Sensor MICS-6814
                </span>
                <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  ESP8266 Wi-Fi
                </span>
                <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  Muestreo cada 20 min
                </span>
                <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  Dashboard Cloud
                </span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Fullscreen Photo Lightbox Modal */}
      <Modal open={open} setOpen={setOpen}>
        <div className="relative bg-slate-900 rounded-2xl p-2 overflow-hidden max-h-[85vh] flex items-center justify-center">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-slate-800/80 text-white flex items-center justify-center hover:bg-slate-700 transition-colors"
          >
            <Icon className="text-sm">xmark</Icon>
          </button>
          <img
            src={src}
            alt="Detalle ampliado"
            className="max-h-[80vh] w-auto object-contain rounded-xl"
          />
        </div>
      </Modal>
    </motion.div>
  );
}